import { AxiosError } from 'axios';
import { DeepPartial } from 'ts-essentials';
import { FNADetails, FnaState } from '../types/analyses';
import { LegalEntity, NaturalPerson } from '../types/client';
import { privateFulcrumApi } from '../lib/fulcrumApi';
import { queryClient } from '../lib/queryClient';
import { Report, ReportState } from '../types/reports';
import { reportBuilderApi } from '../lib/reportBuilderApi';
import { useMutation, UseMutationResult } from '@tanstack/react-query';

/**
 * Hook to create natural person
 */
export const useCreateNaturalPerson = (): UseMutationResult<NaturalPerson> =>
  useMutation({
    mutationFn: (payload: unknown) => {
      return privateFulcrumApi
        .post<NaturalPerson>(`/NaturalPerson`, payload)
        .then(({ data }) => data);
    },
  });

/**
 * Hook to update natural person
 */
export const useUpdateNaturalPerson = (): UseMutationResult<NaturalPerson> =>
  useMutation({
    mutationFn: (payload: unknown) => {
      return privateFulcrumApi
        .put<NaturalPerson>(`/NaturalPerson`, payload)
        .then(({ data }) => data);
    },
    onSuccess: (response) =>
      queryClient.invalidateQueries({
        queryKey: [`/NaturalPerson/${response.roleplayerID}`],
      }),
  });

/**
 * Hook to create legal entity
 */
export const useCreateLegalEntityMutation = (): UseMutationResult<LegalEntity> =>
  useMutation({
    mutationFn: (payload: unknown) => {
      return privateFulcrumApi.post<LegalEntity>(`/Juristic`, payload).then(({ data }) => data);
    },
  });

/**
 * Hook to update legal entity
 */
export const useUpdateLegalEntity = (): UseMutationResult<NaturalPerson> =>
  useMutation({
    mutationFn: (payload: unknown) => {
      return privateFulcrumApi.put<NaturalPerson>(`/Juristic`, payload).then(({ data }) => data);
    },
    onSuccess: (response) =>
      queryClient.invalidateQueries({
        queryKey: [`/Juristic/${response.roleplayerID}`],
      }),
  });

/**
 * Hook to create FNA
 */
export const useCreateNewFna = (): UseMutationResult<FNADetails> =>
  useMutation({
    mutationFn: (payload: unknown) => {
      return privateFulcrumApi.post<FNADetails>(`/FNADetail`, payload).then(({ data }) => data);
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries({
        queryKey: [`/FNADetail/${response.id}`, '/FNALists'],
      });
    },
  });

/**
 * Hook to update FNA
 */
export const useUpdateFna = (): UseMutationResult<FNADetails, unknown, DeepPartial<FNADetails>> =>
  useMutation({
    mutationFn: (payload) => {
      return privateFulcrumApi.put<FNADetails>(`/FNADetail`, payload).then(({ data }) => data);
    },
    onSuccess: (response) =>
      queryClient.invalidateQueries({
        queryKey: [`/FNADetail/${response.id}`],
      }),
  });

/**
 * Hook to create new report
 */
export const useCreateNewReport = (): UseMutationResult<Report, unknown, DeepPartial<Report>> =>
  useMutation({
    mutationFn: async (payload) => {
      return privateFulcrumApi.post<Report>(`/ReportDetail`, payload).then(({ data }) => data);
    },
  });

/**
 * Hook to generate report files
 */
export const useGenerateReportFiles = (): UseMutationResult<void, unknown, number> =>
  useMutation({
    mutationFn: async (payload) => {
      await reportBuilderApi.post(`/api/reports/${payload}`, payload);
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({
        queryKey: [`/ReportDetail/${id}`],
      });
    },
  });

interface UploadResponse {
  url: string;
  name: string;
}

interface UploadFilePayload {
  file: File;
  reportId: number;
}

/**
 * Hook to upload report file
 */
export const useUploadReportFile = (): UseMutationResult<
  UploadResponse[],
  unknown,
  UploadFilePayload
> =>
  useMutation({
    mutationFn: async (payload) => {
      const formData = new FormData();
      formData.append('file', payload.file);
      formData.append('reportId', payload.reportId.toString());
      return reportBuilderApi
        .post<UploadResponse[]>(`/api/upload`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })
        .then(({ data }) => data);
    },
  });

/**
 * Hook to update report
 */
export const useUpdateReport = (): UseMutationResult<Report, unknown, DeepPartial<Report>> =>
  useMutation({
    mutationFn: async (payload) => {
      return privateFulcrumApi.put<Report>(`/ReportDetail`, payload).then(({ data }) => data);
    },
    onSuccess: (data: Report) => {
      queryClient.setQueryData([`/ReportDetail/${data.id}`], data);
    },
  });

export interface RequestQuoteResponse {
  detail: string;
  status: number;
  title: string;
}

/**
 * Hook to request quote
 */
export const useRequestQuote = (): UseMutationResult<RequestQuoteResponse, AxiosError> =>
  useMutation({
    mutationFn: (payload: unknown) => {
      return privateFulcrumApi
        .get<RequestQuoteResponse>(`/FNAQuote/${payload}`)
        .then(({ data }) => data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['/FNAQuoteLists'],
      });
    },
  });

/**
 * Hook to archive FNA
 */
export const useArchiveFna = (): UseMutationResult<FNADetails, AxiosError, number> =>
  useMutation({
    mutationFn: async (fnaId) => {
      const fna = await privateFulcrumApi
        .get<FNADetails>(`/FNADetail/${fnaId}`)
        .then(({ data }) => data);

      return privateFulcrumApi
        .put<FNADetails>(`/FNADetail`, {
          ...fna,
          stateID: FnaState.Archived,
        })
        .then(({ data }) => data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['/FNALists'],
      });
    },
  });

/**
 * Hook to archive Report
 */
export const useArchiveReport = (): UseMutationResult<Report, AxiosError, number> =>
  useMutation({
    mutationFn: async (reportId) => {
      const report = await privateFulcrumApi
        .get<Report>(`/ReportDetail/${reportId}`)
        .then(({ data }) => data);

      return privateFulcrumApi
        .put<Report>(`/ReportDetail`, {
          ...report,
          stateID: ReportState.Archived,
        })
        .then(({ data }) => data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['/Reports'],
      });
    },
  });
